import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';

interface FormattedMessageProps {
  content: string;
  className?: string;
  tone?: 'default' | 'inverse';
}

// Sanitize and normalize model artifacts and unreadable characters
const sanitizeContent = (raw: string): string => {
  if (!raw) return '';
  return raw
    // Remove private-use unicode artifacts like '', '', ''
    .replace(/[\uE000-\uF8FF]/g, '')
    // Remove explicit filecite markers and their tokens
    .replace(/filecite:?[^\s】]*/gi, '')
    // Remove patterns like turn0file1, TURN12FILE3, etc.
    .replace(/\bturn\d+file\d+\b/gi, '')
    // Remove bracketed citations like 【...】 entirely
    .replace(/【[^】]*】/g, '')
    // Remove control chars except tab/newline/carriage return
    .split('').filter(c => { const n = c.charCodeAt(0); return (n > 31 || n === 9 || n === 10 || n === 13) && (n < 127 || n > 159); }).join('')
    // Normalize NBSP and similar spaces
    .replace(/\u00A0/g, ' ')
    // Remove leading banner lines like "Create Artifact Response" or "Deep Thinking Response"
    .replace(/(^|\n)[>\s*_-]*?(?:⚡️|:zap:)?\s*Create Artifact Response\s*(\n|$)/gi, '$1')
    .replace(/(^|\n)[>\s*_-]*?(?:⚡️|:zap:)?\s*Deep Thinking Response\s*(\n|$)/gi, '$1')
    // Trim trailing spaces on each line while keeping newlines
    .split('\n').map((l) => l.replace(/\s+$/g, '')).join('\n')
    .trim();
};

export const FormattedMessage: React.FC<FormattedMessageProps> = ({
  content,
  className = "",
  tone = 'default',
}) => {
  const sanitizedContent = sanitizeContent(content);
  const proseToneClass = tone === 'inverse' ? 'prose-invert' : 'prose-slate dark:prose-invert';
  const textToneClass = tone === 'inverse' ? 'text-primary-foreground' : 'text-foreground';
  const mutedToneClass = tone === 'inverse' ? 'text-primary-foreground/80' : 'text-muted-foreground';
  const codeSurfaceClass = tone === 'inverse'
    ? 'bg-white/10 border-white/15 text-primary-foreground'
    : 'bg-muted border text-foreground';
  const tableBorderClass = tone === 'inverse' ? 'border-white/15' : 'border-border';
  const tableHeaderClass = tone === 'inverse'
    ? 'bg-white/10 text-primary-foreground'
    : 'bg-muted text-foreground';
  const linkToneClass = tone === 'inverse'
    ? 'text-primary-foreground underline decoration-primary-foreground/40 hover:text-primary-foreground/80'
    : 'text-primary hover:text-primary/80 underline';

  return (
    <div className={`prose prose-sm max-w-none ${proseToneClass} ${className}`}>
      <ReactMarkdown 
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={{
          // Custom styling for different elements
          h1: ({ children }) => <h1 className={`text-lg font-bold mb-3 ${textToneClass}`}>{children}</h1>,
          h2: ({ children }) => <h2 className={`text-base font-semibold mb-2 ${textToneClass}`}>{children}</h2>,
          h3: ({ children }) => <h3 className={`text-sm font-semibold mb-2 ${textToneClass}`}>{children}</h3>,
          p: ({ children }) => <p className={`text-sm leading-relaxed mb-3 ${textToneClass} last:mb-0`}>{children}</p>,
          strong: ({ children }) => <strong className={`font-semibold ${textToneClass}`}>{children}</strong>,
          em: ({ children }) => <em className={`italic ${textToneClass}`}>{children}</em>,
          code: ({ children, className }) => {
            const isInline = !className;
            if (isInline) {
              return (
                <code className={`px-1.5 py-0.5 rounded text-xs font-mono ${codeSurfaceClass}`}>
                  {children}
                </code>
              );
            }
            return (
              <code className={`block p-3 rounded-md text-xs font-mono overflow-x-auto ${codeSurfaceClass}`}>
                {children}
              </code>
            );
          },
          pre: ({ children }) => (
            <pre className={`p-3 rounded-md overflow-x-auto mb-3 ${codeSurfaceClass}`}>
              {children}
            </pre>
          ),
          ul: ({ children }) => <ul className={`list-disc list-outside ml-5 mb-3 space-y-2 text-sm ${textToneClass}`}>{children}</ul>,
          ol: ({ children }) => <ol className={`list-decimal list-outside ml-5 mb-3 space-y-2 text-sm ${textToneClass}`}>{children}</ol>,
          li: ({ children }) => (
            <li className={`${textToneClass} leading-relaxed pl-2`}>
              <div className="[&>p]:inline [&>p]:m-0">{children}</div>
            </li>
          ),
          blockquote: ({ children }) => (
            <blockquote className={`border-l-4 pl-4 my-3 italic ${tone === 'inverse' ? 'border-primary-foreground/30' : 'border-primary'} ${mutedToneClass}`}>
              {children}
            </blockquote>
          ),
          a: ({ href, children }) => (
            <a 
              href={href} 
              target="_blank" 
              rel="noopener noreferrer"
              className={linkToneClass}
            >
              {children}
            </a>
          ),
          table: ({ children }) => (
            <div className="overflow-x-auto mb-3">
              <table className={`min-w-full border rounded-md ${tableBorderClass}`}>
                {children}
              </table>
            </div>
          ),
          th: ({ children }) => (
            <th className={`border px-3 py-2 text-left font-semibold text-sm ${tableBorderClass} ${tableHeaderClass}`}>
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className={`border px-3 py-2 text-sm ${tableBorderClass} ${textToneClass}`}>
              {children}
            </td>
          ),
        }}
>
        {sanitizedContent}
      </ReactMarkdown>
    </div>
  );
};
